import { message, Tooltip } from 'antd';
import { useEffect, useMemo, useRef } from 'react';
import { initEcharts, applyChartTheme } from 'lib/echarts';
import styled from '@emotion/styled';
import { formatNumber } from 'utils/numberFormat';
import { getNegativeCategoryOverview } from './service';
import { useTranslate } from '@tolgee/react';
import { useInViewport, useRequest } from 'ahooks';
import { LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import TableContent from './TableContent';
import dayjs from 'dayjs';
import { getChartMaxValue } from '../Activity/useActivityContents';

function NegativeReason({ filter }: { filter: ReviewAnalysis.OverviewFilter }) {
  const { startTime, endTime } = filter;

  const { t } = useTranslate();

  const { loading, data: { problemCategoryList, reviewCountDaily } = {} } = useRequest(
    () => getNegativeCategoryOverview(filter),
    {
      refreshDeps: [filter],
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const { categoryMap } = useMemo(() => {
    const categoryMap = problemCategoryList?.reduce<number[]>((res, { id, problemCount }) => {
      res[id] = problemCount;
      return res;
    }, []);

    return {
      categoryMap,
    };
  }, [problemCategoryList]);

  const chartDomRef = useRef(null);

  const [inViewport] = useInViewport(chartDomRef);

  useEffect(() => {
    if (!inViewport || !chartDomRef.current || !reviewCountDaily) {
      return;
    }

    const start = dayjs(Number(startTime));
    const end = dayjs(Number(endTime));
    let cur = start;
    const days = [cur];
    while (cur.startOf('days').isBefore(end.startOf('days'))) {
      cur = cur.clone().add(1, 'days');
      days.push(cur);
    }

    const dataList = days.map((day) => {
      const date = day.format('YYYYMMDD');
      const target = reviewCountDaily?.find((_) => _.dt === date);
      return [
        target?.totalCount || 0,
        target?.categoryReviewCount.find((_) => _.id === 1)?.reviewCount || 0,
        target?.categoryReviewCount.find((_) => _.id === 2)?.reviewCount || 0,
        target?.categoryReviewCount.find((_) => _.id === 3)?.reviewCount || 0,
        target?.categoryReviewCount.find((_) => _.id === 4)?.reviewCount || 0,
      ];
    });

    const totalList = dataList.map((_) => _[0]);
    const list1 = dataList.map((_) => _[1]);
    const list2 = dataList.map((_) => _[2]);
    const list3 = dataList.map((_) => _[3]);
    const list4 = dataList.map((_) => _[4]);

    const max = getChartMaxValue([...list1, ...list2, ...list3, ...list4], 5);

    const chart = initEcharts(chartDomRef.current);

    chart.setOption(
      applyChartTheme({
        tooltip: {
          trigger: 'axis',
          valueFormatter: (value: number, index: number) =>
            `${value}   ${value ? formatNumber(value / totalList[index], '0.00%') : '0.00%'}`,
        },
        legend: {
          data: [t('knowledge.商品相关'), t('物流配送'), t('服务体验'), t('活动营销')],
          top: 0,
          left: 'center',
        },
        grid: {
          left: '1%',
          right: '2%',
          containLabel: true,
        },
        dataZoom: [
          {
            realtime: true,
            start: 0,
            end: 100,
          },
          {
            type: 'inside',
            realtime: true,
            start: 0,
            end: 100,
          },
        ],
        xAxis: {
          type: 'category',
          data: days.map((day) => day.format('MM-DD')),
        },
        yAxis: [
          {
            type: 'value',
            min: 0,
            max: max,
            interval: max / 5,
          },
        ],
        series: [
          {
            name: t('knowledge.商品相关'),
            type: 'line',
            data: list1,
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: {
              color: '#5B8FF9',
            },
            areaStyle: {
              opacity: 0.2,
            },
          },
          {
            name: t('物流配送'),
            type: 'line',
            data: list2,
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: {
              color: '#5AD8A6',
            },
            areaStyle: {
              opacity: 0.2,
            },
          },
          {
            name: t('服务体验'),
            type: 'line',
            data: list3,
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: {
              color: '#5D7092',
            },
            areaStyle: {
              opacity: 0.2,
            },
          },
          {
            name: t('活动营销'),
            type: 'line',
            data: list4,
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: {
              color: '#F6BD16',
            },
            areaStyle: {
              opacity: 0.2,
            },
          },
        ],
      }),
    );

    const handler = () => {
      chart.resize();
    };

    window.addEventListener('resize', handler);

    return () => {
      window.removeEventListener('resize', handler);

      chart.dispose();
    };
  }, [inViewport, startTime, endTime, reviewCountDaily, t]);

  if (!categoryMap) {
    return loading ? <LoadingOutlined /> : <></>;
  }

  const overview = (
    <section>
      <header>
        <h3>{t('差评趋势')}</h3>
        <Tooltip title={t('差评趋势-tips')}>
          <QuestionCircleOutlined />
        </Tooltip>
      </header>
      <div ref={chartDomRef} />
    </section>
  );

  return (
    <Container>
      {overview}
      <div>
        <TableContent filter={filter} categoryMap={categoryMap} />
      </div>
    </Container>
  );
}

const Container = styled.div`
  background: var(--fett-bg-panel);
  > section {
    position: relative;
    padding: 24px;
    > header {
      display: flex;
      align-items: center;
      position: absolute;
      top: 24px;
      left: 24px;
      z-index: 1;
      > h3 {
        margin-bottom: 0;
        margin-right: 8px;
      }
      > .anticon {
        cursor: pointer;
      }
    }
    > div {
      height: 345px;
    }
  }
  > div {
    padding: 0 16px;
    > header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      > span .ant-switch {
        margin-left: 4px;
      }
    }
  }
`;

export default NegativeReason;
